import { t as tCem } from '../utils/i18n'
/**
 * AuditView — Auditoría de operadores
 *
 * • Tabla de métricas por operador: incidentes manejados, tiempo promedio
 *   de acknowledge y resolución, acciones registradas, último login.
 * • Log de actividad paginado con filtros por usuario, acción y ventana.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Users, Clock, CheckCircle2, Activity, LogIn,
  RefreshCw, Loader2, ChevronLeft, ChevronRight,
  Filter, AlertCircle, Eye, MessageSquare, UserCheck,
  TrendingDown, TrendingUp, Minus, Radio, Play, Download,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtMins(mins) {
  if (mins === null || mins === undefined) return '—'
  if (mins < 1)  return '<1m'
  if (mins < 60) return `${Math.round(mins)}m`
  return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`
}

function fmtDt(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString(tCem('__ES__','__EN__')==='__EN__' ? 'en-US' : 'es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function rolBadge(rol) {
  const colors = {
    superadmin: '#C00000', admin: '#7B4F9E', supervisor: '#2E75B6',
    operator: '#1E7E34', analyst: '#C85A00', guard: '#666',
  }
  return (
    <span style={{
      background: (colors[rol] || '#888') + '22',
      color: colors[rol] || '#888',
      border: `1px solid ${colors[rol] || '#888'}55`,
      borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700,
    }}>{rol}</span>
  )
}

function actionLabel(action) {
  const map = tCem('__ES__','__EN__') === '__EN__' ? {
    login:                    { icon: <LogIn size={13}/>,      label: 'Login',             color: '#2E75B6' },
    logout:                   { icon: <LogIn size={13}/>,      label: 'Logout',            color: '#888' },
    incident_viewed:          { icon: <Eye size={13}/>,        label: 'Viewed incident',   color: '#666' },
    incident_created:         { icon: <AlertCircle size={13}/>,label: 'Created incident',  color: '#C85A00' },
    incident_status_changed:  { icon: <Activity size={13}/>,   label: 'Status change',     color: '#1E3A5F' },
    incident_severity_changed:{ icon: <TrendingUp size={13}/>, label: 'Severity change',   color: '#C00000' },
    incident_assigned:        { icon: <UserCheck size={13}/>,  label: 'Assigned',          color: '#7B4F9E' },
    incident_note_added:      { icon: <MessageSquare size={13}/>,label: 'Note added',      color: '#1E7E34' },
    incident_snapshot_added:  { icon: <Eye size={13}/>,        label: 'Snapshot',          color: '#444' },
  } : {
    login:                    { icon: <LogIn size={13}/>,      label: 'Login',             color: '#2E75B6' },
    logout:                   { icon: <LogIn size={13}/>,      label: 'Logout',            color: '#888' },
    incident_viewed:          { icon: <Eye size={13}/>,        label: 'Vio incidente',     color: '#666' },
    incident_created:         { icon: <AlertCircle size={13}/>,label: 'Creó incidente',    color: '#C85A00' },
    incident_status_changed:  { icon: <Activity size={13}/>,   label: 'Cambio estado',     color: '#1E3A5F' },
    incident_severity_changed:{ icon: <TrendingUp size={13}/>, label: 'Cambio severidad',  color: '#C00000' },
    incident_assigned:        { icon: <UserCheck size={13}/>,  label: 'Asignó',            color: '#7B4F9E' },
    incident_note_added:      { icon: <MessageSquare size={13}/>,label: 'Nota añadida',    color: '#1E7E34' },
    incident_snapshot_added:  { icon: <Eye size={13}/>,        label: 'Snapshot',          color: '#444' },
  }
  const m = map[action] || { icon: <Activity size={13}/>, label: action, color: '#888' }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: m.color, fontSize: 12 }}>
      {m.icon} {m.label}
    </span>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function MetricCell({ value, sub, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: color || 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>}
    </div>
  )
}

function TTABar({ tta, maxTTA = 60 }) {
  if (!tta) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
  const pct = Math.min(100, (tta / maxTTA) * 100)
  const color = tta <= 10 ? '#1E7E34' : tta <= 30 ? '#C85A00' : '#C00000'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s' }}/>
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color, minWidth: 36 }}>{fmtMins(tta)}</span>
    </div>
  )
}

// ─── Main View ───────────────────────────────────────────────────────────────

const HOURS_OPTIONS = [
  { label: '24h', value: 24 },
  { label: '7 days', value: 168 },
  { label: '30 days', value: 720 },
]

const ACTION_OPTIONS = tCem('__ES__','__EN__') === '__EN__' ? [
  { value: '', label: 'All actions' },
  { value: 'login', label: 'Login' },
  { value: 'incident_created', label: 'Created incident' },
  { value: 'incident_status_changed', label: 'Status change' },
  { value: 'incident_severity_changed', label: 'Severity change' },
  { value: 'incident_assigned', label: 'Assignment' },
  { value: 'incident_note_added', label: 'Note added' },
  { value: 'incident_viewed', label: 'Viewed incident' },
] : [
  { value: '', label: 'Todas las acciones' },
  { value: 'login', label: 'Login' },
  { value: 'incident_created', label: 'Creó incidente' },
  { value: 'incident_status_changed', label: 'Cambio de estado' },
  { value: 'incident_severity_changed', label: 'Cambio severidad' },
  { value: 'incident_assigned', label: 'Asignación' },
  { value: 'incident_note_added', label: 'Nota añadida' },
  { value: 'incident_viewed', label: 'Vio incidente' },
]

// ─── RecordingsTab ────────────────────────────────────────────────────────────

function RecordingsTab({ token }) {
  const [records,  setRecords]  = useState([])
  const [loading,  setLoading]  = useState(false)
  const [page,     setPage]     = useState(1)
  const [filter,   setFilter]   = useState('')   // '' | site | zone | all | video
  const [playing,  setPlaying]  = useState(null) // log_id activo
  const audioRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page, page_size: 50 })
      if (filter) params.set('call_type', filter)
      const r = await fetch(`/api/ptt/recordings?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok) setRecords(await r.json())
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [token, page, filter])

  useEffect(() => { load() }, [load])

  function fmtDur(sec) {
    if (!sec) return '—'
    const m = Math.floor(sec / 60), s = sec % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }

  const typeLabel = tCem('__ES__','__EN__') === '__EN__' ? { site: '📻 Site', zone: '📍 Zone', all: '🌐 Global', video: '📹 Video' } : { site: '📻 Sitio', zone: '📍 Zona', all: '🌐 Global', video: '📹 Video' }
  const typeColor = { site: '#3b82f6', zone: '#10b981', all: '#ef4444', video: '#f59e0b' }

  async function playRecording(logId) {
    if (playing === logId) {
      audioRef.current?.pause()
      setPlaying(null)
      return
    }
    setPlaying(logId)
    if (audioRef.current) {
      audioRef.current.src = `/api/ptt/recordings/${logId}/stream`
      audioRef.current.onended = () => setPlaying(null)
      try { await audioRef.current.play() } catch (e) { setPlaying(null) }
    }
  }

  return (
    <div>
      {/* Oculto para reproducción */}
      <audio ref={audioRef} style={{ display: 'none' }}
        onEnded={() => setPlaying(null)} />

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{tCem('Tipo:','Type:')}</span>
        {[
          { value: '',      label: tCem('Todas','All') },
          { value: 'site',  label: tCem('📻 Sitio','📻 Site') },
          { value: 'zone',  label: tCem('📍 Zona','📍 Zone') },
          { value: 'all',   label: '🌐 Global' },
          { value: 'video', label: '📹 Video' },
        ].map(o => (
          <button key={o.value} onClick={() => { setFilter(o.value); setPage(1) }} style={{
            padding: '5px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
            border: '1px solid var(--border)',
            background: filter === o.value ? 'var(--accent)' : 'var(--bg-surface)',
            color: filter === o.value ? 'white' : 'var(--text-primary)',
            fontWeight: filter === o.value ? 700 : 400,
          }}>{o.label}</button>
        ))}
        <button onClick={load} style={{
          marginLeft: 'auto', padding: '5px 12px', borderRadius: 6, fontSize: 13,
          border: '1px solid var(--border)', background: 'var(--bg-surface)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
          color: 'var(--text-muted)',
        }}>
          <RefreshCw size={13}/> {tCem('Actualizar','Refresh')}
        </button>
      </div>

      {/* Tabla */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[tCem('Fecha','Date'), tCem('Tipo','Type'), tCem('Destino','Target'), tCem('Operador','Operator'), tCem('Duración','Duration'), tCem('Grabación','Recording')].map(h => (
                <th key={h} style={{
                  padding: '10px 14px', fontSize: 11, fontWeight: 700,
                  color: 'var(--text-muted)', textTransform: 'uppercase',
                  letterSpacing: '0.5px', borderBottom: '1px solid var(--border)',
                  background: 'var(--bg-base)', textAlign: 'left', whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                Cargando…
              </td></tr>
            )}
            {!loading && records.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                Sin grabaciones para este filtro
              </td></tr>
            )}
            {records.map(r => (
              <tr key={r.id}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>
                  {r.started_at ? new Date(r.started_at).toLocaleString('es-CL', {
                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                  }) : '—'}
                </td>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 700,
                    background: (typeColor[r.call_type] || '#888') + '22',
                    color: typeColor[r.call_type] || '#888',
                  }}>{typeLabel[r.call_type] || r.call_type}</span>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                  {r.target_name || <span style={{ color: 'var(--text-muted)' }}>{tCem('Toda la red','All network')}</span>}
                </td>
                <td style={{ padding: '10px 14px', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                  {r.caller_name || '—'}
                </td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                  {fmtDur(r.duration_sec)}
                </td>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                  {r.has_recording && r.recording_ready ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => playRecording(r.id)}
                        title={playing === r.id ? tCem('Pausar','Pause') : tCem('Reproducir','Play')}
                        style={{
                          padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                          background: playing === r.id ? 'var(--danger)' : 'var(--accent)',
                          color: 'white', fontSize: 11, fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <Play size={11}/> {playing === r.id ? 'Pause' : 'Play'}
                      </button>
                      <a
                        href={`/api/ptt/recordings/${r.id}/stream`}
                        download
                        style={{
                          padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)',
                          background: 'var(--bg-surface)', color: 'var(--text-muted)',
                          fontSize: 11, fontWeight: 600, textDecoration: 'none',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <Download size={11}/> OGG
                      </a>
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {r.has_recording ? 'Procesando…' : '—'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 14 }}>
        <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
          style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
            background: 'var(--bg-surface)', cursor: page === 1 ? 'default' : 'pointer',
            opacity: page === 1 ? 0.4 : 1 }}>
          <ChevronLeft size={14}/>
        </button>
        <span style={{ fontSize: 13, color: 'var(--text-muted)', alignSelf: 'center' }}>Página {page}</span>
        <button disabled={records.length < 50} onClick={() => setPage(p => p + 1)}
          style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
            background: 'var(--bg-surface)', cursor: records.length < 50 ? 'default' : 'pointer',
            opacity: records.length < 50 ? 0.4 : 1 }}>
          <ChevronRight size={14}/>
        </button>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
        Retención: 90 días. Las grabaciones se eliminan automáticamente pasado ese período.
      </div>
    </div>
  )
}

// ─── Main View ────────────────────────────────────────────────────────────────

export default function AuditView() {
  const { token } = useAuthStore()
  const [tab, setTab] = useState('operators')  // 'operators' | 'log' | 'recordings'

  // Operators state
  const [hours, setHours]           = useState(168)
  const [operators, setOperators]   = useState([])
  const [loadingOps, setLoadingOps] = useState(false)

  // Log state
  const [logItems, setLogItems]     = useState([])
  const [logTotal, setLogTotal]     = useState(0)
  const [logPage, setLogPage]       = useState(0)
  const [logLimit]                  = useState(30)
  const [filterUserId, setFilterUserId] = useState('')
  const [filterAction, setFilterAction] = useState('')
  const [logHours, setLogHours]     = useState(168)
  const [loadingLog, setLoadingLog] = useState(false)
  const [userList, setUserList]     = useState([])

  const api = useCallback(async (path) => {
    const r = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
    if (!r.ok) throw new Error(await r.text())
    return r.json()
  }, [token])

  // Load operators
  const loadOperators = useCallback(async () => {
    setLoadingOps(true)
    try {
      const data = await api(`/reports/operators?hours=${hours}`)
      setOperators(data.operators || [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingOps(false)
    }
  }, [api, hours])

  // Load audit log
  const loadLog = useCallback(async () => {
    setLoadingLog(true)
    try {
      const params = new URLSearchParams({
        hours: logHours, limit: logLimit, offset: logPage * logLimit,
      })
      if (filterUserId) params.set('user_id', filterUserId)
      if (filterAction)  params.set('action', filterAction)
      const data = await api(`/reports/audit-log?${params}`)
      setLogItems(data.items || [])
      setLogTotal(data.total || 0)
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingLog(false)
    }
  }, [api, logHours, logLimit, logPage, filterUserId, filterAction])

  // Load user list for filter dropdown
  const loadUsers = useCallback(async () => {
    try {
      const data = await api('/users/')
      setUserList(data.items || data || [])
    } catch {}
  }, [api])

  useEffect(() => { loadOperators() }, [loadOperators])
  useEffect(() => { loadLog() },       [loadLog])
  useEffect(() => { loadUsers() },     [loadUsers])

  // ── Styles ──────────────────────────────────────────────────────────────

  const card = {
    background: 'var(--bg-surface)', border: '1px solid var(--border)',
    borderRadius: 10, overflow: 'hidden',
  }
  const th = {
    padding: '10px 14px', fontSize: 11, fontWeight: 700,
    color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: '1px solid var(--border)', background: 'var(--bg-base)',
    whiteSpace: 'nowrap',
  }
  const td = {
    padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border)',
  }

  // ── Max TTA for bar scaling ──────────────────────────────────────────────
  const maxTTA = Math.max(60, ...operators.map(o => o.avg_tta_min || 0))

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            {(typeof window!=='undefined'&&window.location.hostname==='cad.retaliq.net') ? 'Operator Audit' : 'Auditoría de Operadores'}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>
            {(typeof window!=='undefined'&&window.location.hostname==='cad.retaliq.net')
              ? 'Activity, response times and action traceability'
              : 'Actividad, tiempos de respuesta y trazabilidad de acciones'}
          </p>
        </div>
        <button
          onClick={() => { loadOperators(); loadLog() }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'var(--accent)', color: 'white',
            border: 'none', borderRadius: 8, padding: '8px 16px',
            cursor: 'pointer', fontSize: 13, fontWeight: 600,
          }}
        >
          <RefreshCw size={14}/> {(typeof window!=='undefined'&&window.location.hostname==='cad.retaliq.net') ? 'Refresh' : 'Actualizar'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {((typeof window!=='undefined'&&window.location.hostname==='cad.retaliq.net') ? [
          { id: 'operators',  label: 'Operator Metrics',   icon: <Users size={15}/> },
          { id: 'log',        label: 'Activity Log',       icon: <Activity size={15}/> },
          { id: 'recordings', label: 'PTT Recordings',     icon: <Radio size={15}/> },
        ] : [
          { id: 'operators',  label: 'Métricas por Operador', icon: <Users size={15}/> },
          { id: 'log',        label: 'Log de Actividad',      icon: <Activity size={15}/> },
          { id: 'recordings', label: 'Grabaciones PTT',       icon: <Radio size={15}/> },
        ]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '9px 18px', border: 'none', background: 'none',
            cursor: 'pointer', fontSize: 13, fontWeight: tab === t.id ? 700 : 400,
            color: tab === t.id ? 'var(--accent)' : 'var(--text-muted)',
            borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -1,
          }}>{t.icon}{t.label}</button>
        ))}
      </div>

      {/* ── TAB: OPERATORS ── */}
      {tab === 'operators' && (
        <>
          {/* Controls */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Period:</span>
            {HOURS_OPTIONS.map(o => (
              <button key={o.value} onClick={() => setHours(o.value)} style={{
                padding: '5px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                border: '1px solid var(--border)',
                background: hours === o.value ? 'var(--accent)' : 'var(--bg-surface)',
                color: hours === o.value ? 'white' : 'var(--text-primary)',
                fontWeight: hours === o.value ? 700 : 400,
              }}>{o.label}</button>
            ))}
            {loadingOps && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent)' }}/>}
          </div>

          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              {
                icon: <Users size={18}/>,
                label: 'Active operators',
                value: operators.filter(o => o.action_count > 0).length,
                color: '#2E75B6',
              },
              {
                icon: <CheckCircle2 size={18}/>,
                label: 'Resolved incidents',
                value: operators.reduce((s, o) => s + o.incidents_resolved, 0),
                color: '#1E7E34',
              },
              {
                icon: <Clock size={18}/>,
                label: 'Global avg TTA',
                value: (() => {
                  const vals = operators.map(o => o.avg_tta_min).filter(Boolean)
                  return vals.length ? fmtMins(vals.reduce((a, b) => a + b, 0) / vals.length) : '—'
                })(),
                color: '#C85A00',
              },
              {
                icon: <Activity size={18}/>,
                label: 'Total actions',
                value: operators.reduce((s, o) => s + o.action_count, 0),
                color: '#7B4F9E',
              },
            ].map((c, i) => (
              <div key={i} style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ color: c.color, opacity: 0.85 }}>{c.icon}</div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Operators table */}
          <div style={card}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: "left" }}>Operator</th>
                  <th style={th}>Role</th>
                  <th style={th}>Incidents touched</th>
                  <th style={th}>Resolved</th>
                  <th style={th}>Actions</th>
                  <th style={{ ...th, minWidth: 160 }}>Avg TTA</th>
                  <th style={th}>Avg TTR</th>
                  <th style={th}>Logins</th>
                  <th style={{ ...th, textAlign: "left" }}>Last action</th>
                </tr>
              </thead>
              <tbody>
                {operators.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>
                      {loadingOps ? tCem('Cargando…','Loading…') : tCem('Sin datos para este período','No data for this period')}
                    </td>
                  </tr>
                )}
                {operators.map(op => (
                  <tr key={op.user_id} style={{ cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                    onClick={() => { setTab('log'); setFilterUserId(String(op.user_id)); setLogPage(0) }}
                  >
                    <td style={{ ...td }}>
                      <div style={{ fontWeight: 600 }}>{op.nombre}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{op.email}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>{rolBadge(op.rol)}</td>
                    <td style={{ ...td }}>
                      <MetricCell
                        value={op.incidents_touched}
                        color={op.incidents_touched > 0 ? 'var(--text-primary)' : 'var(--text-muted)'}
                      />
                    </td>
                    <td style={{ ...td }}>
                      <MetricCell
                        value={op.incidents_resolved}
                        color={op.incidents_resolved > 0 ? '#1E7E34' : 'var(--text-muted)'}
                      />
                    </td>
                    <td style={{ ...td }}>
                      <MetricCell value={op.action_count} />
                    </td>
                    <td style={{ ...td, minWidth: 160 }}>
                      <TTABar tta={op.avg_tta_min} maxTTA={maxTTA} />
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{
                        fontSize: 12, fontWeight: 600,
                        color: !op.avg_ttr_min ? 'var(--text-muted)' :
                               op.avg_ttr_min <= 15 ? '#1E7E34' :
                               op.avg_ttr_min <= 45 ? '#C85A00' : '#C00000',
                      }}>{fmtMins(op.avg_ttr_min)}</span>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{op.login_count}</span>
                    </td>
                    <td style={{ ...td }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDt(op.last_action_at)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            TTA = Time to first acknowledge · TTR = Time to resolution · Click a row to see the operator's log
          </p>
        </>
      )}

      {/* ── TAB: LOG ── */}
      {tab === 'log' && (
        <>
          {/* Filters */}
          <div style={{
            display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center',
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '12px 16px',
          }}>
            <Filter size={15} style={{ color: 'var(--text-muted)' }}/>
            <select
              value={filterUserId}
              onChange={e => { setFilterUserId(e.target.value); setLogPage(0) }}
              style={{
                padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)',
                background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 13,
              }}
            >
              <option value="">Todos los operadores</option>
              {userList.map(u => (
                <option key={u.id} value={u.id}>{u.nombre} ({u.rol})</option>
              ))}
            </select>
            <select
              value={filterAction}
              onChange={e => { setFilterAction(e.target.value); setLogPage(0) }}
              style={{
                padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)',
                background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 13,
              }}
            >
              {ACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Period:</span>
            {HOURS_OPTIONS.map(o => (
              <button key={o.value} onClick={() => { setLogHours(o.value); setLogPage(0) }} style={{
                padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                border: '1px solid var(--border)',
                background: logHours === o.value ? 'var(--accent)' : 'var(--bg-base)',
                color: logHours === o.value ? 'white' : 'var(--text-primary)',
              }}>{o.label}</button>
            ))}
            {(filterUserId || filterAction) && (
              <button onClick={() => { setFilterUserId(''); setFilterAction(''); setLogPage(0) }}
                style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: '1px solid var(--border)', background: 'none', color: '#C00000' }}>
                ✕ Limpiar filtros
              </button>
            )}
            {loadingLog && <Loader2 size={15} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent)' }}/>}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
              {logTotal} registros
            </span>
          </div>

          {/* Log table */}
          <div style={card}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>{tCem('Fecha y hora','Date and time')}</th>
                  <th style={{ ...th, textAlign: "left" }}>Operator</th>
                  <th style={th}>Acción</th>
                  <th style={th}>Incidente</th>
                  <th style={{ ...th, textAlign: 'left' }}>{tCem('Detalle','Detail')}</th>
                  <th style={th}>IP</th>
                </tr>
              </thead>
              <tbody>
                {logItems.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>
                      {loadingLog ? tCem('Cargando…','Loading…') : tCem('Sin registros para estos filtros','No records for these filters')}
                    </td>
                  </tr>
                )}
                {logItems.map(item => (
                  <tr key={item.id}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {fmtDt(item.created_at)}
                    </td>
                    <td style={{ ...td }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{item.user_nombre}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.user_rol}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>{actionLabel(item.action)}</td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {item.incident_id
                        ? <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>#{item.incident_id}</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      }
                    </td>
                    <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)', maxWidth: 300 }}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.detail || '—'}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
                      {item.ip_address || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {logTotal > logLimit && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 14 }}>
              <button
                disabled={logPage === 0}
                onClick={() => setLogPage(p => p - 1)}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
                  background: 'var(--bg-surface)', cursor: logPage === 0 ? 'default' : 'pointer',
                  opacity: logPage === 0 ? 0.4 : 1 }}
              ><ChevronLeft size={14}/></button>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Página {logPage + 1} de {Math.ceil(logTotal / logLimit)}
              </span>
              <button
                disabled={(logPage + 1) * logLimit >= logTotal}
                onClick={() => setLogPage(p => p + 1)}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
                  background: 'var(--bg-surface)', cursor: (logPage + 1) * logLimit >= logTotal ? 'default' : 'pointer',
                  opacity: (logPage + 1) * logLimit >= logTotal ? 0.4 : 1 }}
              ><ChevronRight size={14}/></button>
            </div>
          )}
        </>
      )}

      {/* ── TAB: RECORDINGS ── */}
      {tab === 'recordings' && <RecordingsTab token={token} />}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        tr:hover td { background: var(--bg-hover, rgba(0,0,0,0.03)); }
      `}</style>
    </div>
  )
}
